import { useCallback } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { createBroadcastChannelFactory } from './broadcast-channel-factory';
import { createCrossTabBroadcaster } from './session-core';
import { sessionChannelName } from './use-session-broadcast';
import type { CrossTabBroadcaster } from './session-core';
import type { OrganizationSelection } from './types';

type UseOrgSyncOptions = {
  selectedOrganization: OrganizationSelection | null | undefined;
  onSeed: (selection: OrganizationSelection) => void;
  onResync?: (selection: OrganizationSelection) => void;
};

type UseOrgSyncResult = {
  broadcastOrgSwitch: (selection: OrganizationSelection) => void;
  reactToOrganizationSelection: (selection: OrganizationSelection) => void;
};

function FormatSelection(selection: OrganizationSelection | null | undefined): string {
  let key = '';
  if (selection && typeof selection.name === 'string' && selection.name.length > 0) {
    let precedence = 0;
    if (typeof selection.precedence === 'number' && Number.isFinite(selection.precedence)) {
      precedence = selection.precedence;
    }
    key = `${precedence}:${selection.name}`;
  }
  return key;
}

export function useOrgSync({ selectedOrganization, onSeed, onResync }: UseOrgSyncOptions): UseOrgSyncResult {
  const selectedRef = useRef(selectedOrganization);
  useEffect(() => {
    selectedRef.current = selectedOrganization;
  }, [selectedOrganization]);

  const onSeedRef = useRef(onSeed);
  useEffect(() => {
    onSeedRef.current = onSeed;
  }, [onSeed]);

  const onResyncRef = useRef(onResync);
  useEffect(() => {
    onResyncRef.current = onResync;
  }, [onResync]);

  const reloadTriggeredRef = useRef(false);
  const broadcasterRef = useRef<CrossTabBroadcaster | null>(null);

  const reactToOrganizationSelection = useCallback((selection: OrganizationSelection | null | undefined): void => {
    if (!reloadTriggeredRef.current && selection) {
      const currentSelection = selectedRef.current;
      if (!currentSelection) {
        onSeedRef.current(selection);
      } else if (FormatSelection(currentSelection) !== FormatSelection(selection)) {
        const resync = onResyncRef.current;
        if (resync) {
          resync(selection);
        } else {
          reloadTriggeredRef.current = true;
          window.location.reload();
        }
      }
    }
  }, []);

  useEffect(() => {
    const broadcaster = createCrossTabBroadcaster({
      channelName: sessionChannelName,
      createChannel: createBroadcastChannelFactory,
      onOrgSwitch: (selection) => {
        reactToOrganizationSelection(selection);
      },
    });
    broadcasterRef.current = broadcaster;

    return () => {
      broadcaster.close();
      broadcasterRef.current = null;
    };
  }, [reactToOrganizationSelection]);

  const broadcastOrgSwitch = useCallback((selection: OrganizationSelection): void => {
    if (broadcasterRef.current) {
      broadcasterRef.current.broadcastOrgSwitch(selection);
    }
  }, []);

  return {
    broadcastOrgSwitch: broadcastOrgSwitch,
    reactToOrganizationSelection: reactToOrganizationSelection,
  };
}

export function broadcastOrgSwitch(selection: OrganizationSelection): void {
  const broadcaster = createCrossTabBroadcaster({
    channelName: sessionChannelName,
    createChannel: createBroadcastChannelFactory,
  });
  broadcaster.broadcastOrgSwitch(selection);
  broadcaster.close();
}
